/* eslint-disable */
import {
  FieldType,
  FormField2,
  IDynamicFormValue,
  IMultiSelectControlField2,
  ISelectOption
} from 'src/lib';
import _ from 'lodash';
import {
  ICombinationModel,
  IFieldToOptionsMap,
  IFilterObj
} from '../models/filter-response.model';

export function getNextValues(
  fieldName: string,
  fieldValues: ISelectOption[],
  filterFields: FormField2[],
  combinations: ICombinationModel[],
  formValues: IDynamicFormValue
): {
  newFormValues: IDynamicFormValue;
  /**
   * A map object for select fields and their dropdown options
   */
  fieldNameToOptionsMap: IFieldToOptionsMap;
} {
  let newFormValues: IDynamicFormValue = { ...formValues };
  const changedFilterIndex = filterFields.findIndex(
    (filter) => filter.name === fieldName
  );
  const fieldData = filterFields.find((field) => field.name === fieldName);
  const lastItemIndex = filterFields.length - 1;
  if (
    changedFilterIndex > -1 &&
    changedFilterIndex !== lastItemIndex &&
    fieldData.is_hierarchical
  ) {
    const changedField: IMultiSelectControlField2 =
      filterFields[changedFilterIndex];
    const precedingFilterValues = filterFields
      .slice(0, changedFilterIndex)
      .reduce<{
        [filterName: string]: ISelectOption[];
      }>(
        (previousValue, currentValue) => ({
          ...previousValue,
          ...{ [currentValue.name]: formValues[currentValue.name] }
        }),
        {}
      );
    precedingFilterValues[fieldName] =
      changedField.limit !== null && fieldValues.length > changedField.limit
        ? fieldValues.slice(0, changedField.limit)
        : fieldValues;
    const remainingFormValues: {
      [filterName: string]: ISelectOption[];
    } = {};
    const fieldNameToOptionsMap: { [filterName: string]: ISelectOption[] } = {};
    for (
      let index = changedFilterIndex + 1;
      index < filterFields.length;
      index++
    ) {
      const filterField = filterFields[index] as IMultiSelectControlField2;
      if (filterField.is_hierarchical) {
        const newCombinations = combinations.filter((filterCombination) => {
          const keys = Object.keys(precedingFilterValues).filter((key) => {
            const fields = filterFields.find((field) => field.name === key);
            return fields.is_hierarchical;
          });
          return (
            keys.filter((key) =>
              precedingFilterValues[key]
                .map((val) => val.value)
                .includes(filterCombination[key] as string)
            ).length === keys.length
          );
        });
        const dropdownOptionsForFilter = newCombinations
          .map<string>(
            (filterCombination) => filterCombination[filterField.name] as string
          )
          .filter((value) => !!value)
          .filter(
            (value, valueIndex, self) => self.indexOf(value) === valueIndex
          )
          .map<ISelectOption>((val) => ({
            label: val,
            value: val
          }));
        precedingFilterValues[filterField.name] =
          dropdownOptionsForFilter.slice(
            0,
            filterField.is_auto_fill
              ? 1
              : filterField.limit === null
              ? undefined
              : filterField.limit
          );
        fieldNameToOptionsMap[filterField.name] =
          dropdownOptionsForFilter.slice();
      } else {
        precedingFilterValues[filterField.name] = formValues[filterField.name];
      }
    }
    newFormValues = {
      ...precedingFilterValues,
      ...remainingFormValues
    };
    return {
      newFormValues,
      fieldNameToOptionsMap
    };
  } else {
    newFormValues = { ...formValues, [fieldName]: fieldValues };
  }
  if (lastItemIndex === changedFilterIndex) {
    const previousValues = formValues;
    newFormValues = { ...previousValues, [fieldName]: fieldValues };
  }
  return { newFormValues, fieldNameToOptionsMap: {} };
}

/**
 * A helper function to generate map of form field name and its default options
 * @param formFields Form Fields
 * @param combinations Combinations for generating default options
 * @param formValue Current value of the form
 * @returns An object containing map of field name with their default options
 */

export function getDefaultOptionsForFormFields(
  formFields: FormField2[],
  combinations: ICombinationModel[],
  formValue: IDynamicFormValue
) {
  const fieldNameToOptionsMap: { [filterName: string]: ISelectOption[] } = {};
  for (let index = 0; index < formFields.length; index++) {
    const filterField = formFields[index] as IMultiSelectControlField2;
    if (filterField.is_hierarchical) {
      const precedingFilterFields = formFields
        .slice(0, index)
        .filter((e) => e.is_hierarchical)
        .map((e) => e.name);
      const newCombinations = combinations.filter((filterCombination) => {
        const keys = Object.keys(formValue)
          .filter((key) => precedingFilterFields.includes(key))
          .filter((key) => {
            const fields = formFields.find((field) => field.name === key);
            return fields.is_hierarchical;
          });
        return (
          keys.filter((key) =>
            formValue[key]
              .map((val) => val.value)
              .includes(filterCombination[key] as string)
          ).length === keys.length
        );
      });
      const dropdownOptionsForFilter = newCombinations
        .map<string>(
          (filterCombination) => filterCombination[filterField.name] as string
        )
        .filter((value) => !!value)
        .filter((value, valueIndex, self) => self.indexOf(value) === valueIndex)
        .map<ISelectOption>((val) => ({
          label: val,
          value: val
        }));

      fieldNameToOptionsMap[filterField.name] =
        dropdownOptionsForFilter.slice();
    }
  }

  return fieldNameToOptionsMap;
}

export function getDefaultFormValuesAndFormFields(
  formFields: FormField2[],
  combinations: ICombinationModel[],
  initialValues: IDynamicFormValue
) {
  // const fieldIndex = formFields.findIndex(
  //   (field) => (field as IMultiSelectControlField2).limit
  // );

  const fieldIndex = 0;
  if (fieldIndex > -1) {
    const field = formFields[fieldIndex] as IMultiSelectControlField2;
    const { newFormValues, fieldNameToOptionsMap } = getNextValues(
      field.name,
      (field.default_value as ISelectOption[]).slice(
        0,
        field.limit !== null
          ? field.limit
          : (field.default_value as ISelectOption[]).length
      ),
      formFields,
      combinations,
      initialValues
    );
    const formFieldsWithUpdatedOptions = formFields.map((filter) => {
      if (fieldNameToOptionsMap[filter.name]) {
        return { ...filter, values: fieldNameToOptionsMap[filter.name] };
      }
      return filter;
    });
    return {
      formFieldsWithUpdatedOptions,
      newFormValues
    };
  }
  return {
    formFieldsWithUpdatedOptions: formFields,
    newFormValues: initialValues
  };
}

/**
 * Converts the form value of filters into a payload format required by backend APIs
 * For multi select field if the selected options length is equal to all the options available,
 * then that field is not added in the payload format
 * @returns {IFilterObj[]}
 */
export const transformDynamicFormValueToFilterPayload = ({
  combinations,
  formFields,
  formValue
}: {
  formValue: IDynamicFormValue;
  formFields: { name?: string; control_type?: FieldType }[];
  combinations: ICombinationModel[];
}): IFilterObj[] =>
  Object.keys(formValue || {})
    .map<IFilterObj>((fieldName) => {
      const matchingField = formFields.find(
        (field) => field.name === fieldName
      );

      switch (matchingField.control_type) {
        case 'DATE-RANGE':
          return {
            field_name: fieldName,
            control_type: matchingField.control_type,
            field_values: {
              start_date:
                formValue[fieldName] && formValue[fieldName].length
                  ? formValue[fieldName][0].toISOString()
                  : null,
              end_date:
                formValue[fieldName] && formValue[fieldName].length
                  ? formValue[fieldName][1].toISOString()
                  : null
            }
          };

        case 'SINGLESELECT-DROPDOWN':
          return {
            field_name: fieldName,
            control_type: matchingField.control_type,
            field_values: (formValue[fieldName] as ISelectOption[]).map(
              (option) => option.value
            )
          };

        default:
          /**
           * If all of the options from the dropdown are selected
           * mark the value as empty (At backend it is assumed that if a value comes empty then
           * it means all of the options for it are selected)
           */
          const allOptions = combinations
            .map((combination) => combination[fieldName])
            .filter((combination) => !!combination)
            .filter(
              (combination, index, self) => self.indexOf(combination) === index
            );
          const values = formValue[fieldName] as ISelectOption[];
          if (!values.length || allOptions.length === values.length) {
            return null;
          }
          return {
            field_name: fieldName,
            control_type: matchingField.control_type,
            field_values: (formValue[fieldName] as ISelectOption[]).map(
              (option) => option.value
            )
          };
      }
    })
    .filter((field) => !!field);

export const createFieldToOptionsMap = (
  fields: FormField2[],
  combinations: ICombinationModel[]
) =>
  fields
    .filter((field) => field.is_auto_fill)
    .map((field) => ({
      fieldName: field.name,
      fieldOptions: combinations
        .map((combination) => combination[field.name])
        .filter((value, index, self) => self.indexOf(value) === index)
        .map<ISelectOption>((value) => ({
          label: value as string,
          value: value as string
        }))
    }))
    .reduce<{
      [fieldName: string]: ISelectOption[];
    }>((prev, curr) => ({ ...prev, [curr.fieldName]: curr.fieldOptions }), {});

export const getAutoFillValue = (
  combinations: ICombinationModel[],
  baseFilterKey: string,
  baseFilterValue: string,
  autoFillKeys: string[]
) =>
  combinations
    .filter((combination) => combination[baseFilterKey] === baseFilterValue)
    .map((_combination, _index, filteredCombinations) =>
      autoFillKeys.map((autoFillKey) => ({
        fieldName: autoFillKey,
        fieldValues: filteredCombinations
          .map((filteredCombination) => filteredCombination[autoFillKey])
          .filter(
            (filteredCombination, filteredCombinationIndex, self) =>
              self.indexOf(filteredCombination) === filteredCombinationIndex
          )
      }))
    )
    .flat(1)
    .reduce(
      (prev, curr) => ({ ...prev, [curr.fieldName]: curr.fieldValues }),
      {}
    );
